import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Autocomplete,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  AvatarGroup,
  Collapse,
  Fade,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import { useSnackbar } from "notistack";
import { usePlans, useCreatePlan } from "../../hooks/usePlans";
import { usePlanSummary, useCreateList, useAddInfluencersByRapidId } from "../../hooks/usePlanSummary";
import { validatePlanName, validateBrand, hasErrors } from "./validation";
import paymentportalservice from "../../services/paymentportalservice";

const PLATFORM_META = {
  instagram: { Icon: InstagramIcon, label: "Instagram", color: "#E1306C" },
  youtube: { Icon: YouTubeIcon, label: "YouTube", color: "#FF0000" },
};

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str);

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const sectionLabelSx = {
  mb: 1,
  color: "#374151",
  fontWeight: 600,
  fontSize: 13.5,
};

const fieldSx = {
  mb: 2.5,
  "& .MuiOutlinedInput-root": {
    background: "#F9FAFB",
    borderRadius: "10px",
    fontSize: 14.5,
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#D1D5DB" },
    "&.Mui-focused fieldset": { borderColor: "#FF5200", borderWidth: "1.5px" },
    "&.Mui-error fieldset": { borderColor: "#EF4444" },
  },
};

const stepCardSx = {
  position: "relative",
  border: "1px solid #F0F0F3",
  borderRadius: "16px",
  p: 2.5,
  pt: 2.25,
  background: "linear-gradient(180deg, #FCFCFD 0%, #FAFAFB 100%)",
  boxShadow: "0 1px 2px rgba(16,24,40,0.03)",
  animation: `${fadeSlideIn} 0.35s ease both`,
};

const linkButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13.5,
  color: "#FF5200",
  borderRadius: "8px",
  "&:hover": { background: "rgba(255,82,0,0.06)" },
};

const primaryButtonSx = {
  textTransform: "none",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: 13.5,
  background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
  boxShadow: "0 4px 14px rgba(255,82,0,0.3)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
    boxShadow: "0 6px 18px rgba(255,82,0,0.4)",
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": { background: "#F3F4F6", color: "#9CA3AF", boxShadow: "none" },
};

// The small gradient circle with a step number, or a checkmark once that
// step's choice is confirmed — the visual anchor of the two-step "stepper".
const StepBadge = ({ number, done }) => (
  <Box
    sx={{
      width: 26,
      height: 26,
      flexShrink: 0,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 13,
      fontWeight: 700,
      color: "#fff",
      background: done
        ? "linear-gradient(135deg, #059669 0%, #10B981 100%)"
        : "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
      boxShadow: done ? "0 2px 8px rgba(5,150,105,0.35)" : "0 2px 8px rgba(255,82,0,0.35)",
      transition: "background 0.25s ease",
    }}
  >
    {done ? <CheckCircleRoundedIcon sx={{ fontSize: 17 }} /> : number}
  </Box>
);

// Once a plan/list is picked (not mid-create), collapse the picker into a
// calm "confirmed" summary row with an edit affordance — the same pattern
// checkout/onboarding flows use so a finished step doesn't keep competing
// for attention with the one still in progress.
const ConfirmedRow = ({ title, subtitle, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.5,
      p: 1.5,
      borderRadius: "12px",
      background: "rgba(16,185,129,0.06)",
      border: "1px solid rgba(16,185,129,0.22)",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      <CheckCircleIcon sx={{ fontSize: 20, color: "#059669", flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: "#6B7280" }}>{subtitle}</Typography>}
      </Box>
    </Box>
    <Button
      size="small"
      onClick={onChange}
      startIcon={<EditRoundedIcon sx={{ fontSize: 14 }} />}
      sx={{ ...linkButtonSx, color: "#059669", fontSize: 12.5, flexShrink: 0, "&:hover": { background: "rgba(5,150,105,0.08)" } }}
    >
      Change
    </Button>
  </Box>
);

// Friendly empty-state block — mirrors the "No creators found" pattern
// already used on the discovery page itself, for visual consistency.
const EmptyHint = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", py: 3, px: 2 }}>
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: "50%",
        background: "linear-gradient(135deg, rgba(255,82,0,0.1) 0%, rgba(255,140,0,0.08) 100%)",
        border: "2px dashed rgba(255,82,0,0.25)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1.25,
      }}
    >
      <Icon sx={{ fontSize: 24, color: "#FF5200", opacity: 0.8 }} />
    </Box>
    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#374151" }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: 12.5, color: "#9CA3AF", mt: 0.25, maxWidth: 260 }}>{subtitle}</Typography>}
  </Box>
);

/**
 * Two steps in one scrolling drawer body (matches this app's existing drawer
 * convention — no wizard/Next-Back chrome): pick or create a Plan, then pick
 * or create a List within it. Adds by rapidInfluencerId directly — the caller
 * already has these from the discovery page's own RapidInfluencer-backed table.
 */
const AddToPlanDrawer = ({ open, onClose, selectedCreators = [], platform = "instagram", onDone }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();

  const platformMeta = PLATFORM_META[platform] || PLATFORM_META.instagram;
  const rapidInfluencerIds = useMemo(() => selectedCreators.map((c) => c.id), [selectedCreators]);

  // ── Step 1: Plan ────────────────────────────────────────────────────────
  const [planMode, setPlanMode] = useState("pick"); // 'pick' | 'create'
  const [selectedPlan, setSelectedPlan] = useState(null); // {id, name, ...} from usePlans
  const [newPlanName, setNewPlanName] = useState("");
  const [newPlanBrand, setNewPlanBrand] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [planTouched, setPlanTouched] = useState({ planName: false, brand: false });

  const { data: plansData, isLoading: plansLoading } = usePlans("active");
  const plans = plansData?.plans || [];
  const { mutate: createPlan, isPending: creatingPlan } = useCreatePlan();

  const planClientErrors = useMemo(() => ({
    planName: validatePlanName(newPlanName, plans.map((p) => p.name)),
    brand: validateBrand(newPlanBrand),
  }), [newPlanName, newPlanBrand, plans]);
  const isNewPlanValid = !hasErrors(planClientErrors);

  // No plans to pick from at all — go straight to the create form instead of
  // showing a search box with nothing to search.
  useEffect(() => {
    if (!plansLoading && plans.length === 0 && !selectedPlan) setPlanMode("create");
  }, [plansLoading, plans.length, selectedPlan]);

  useEffect(() => {
    if (!open || planMode !== "create") return;
    let isMounted = true;
    setLoadingBrands(true);
    paymentportalservice.FetchBrandsData()
      .then((raw) => {
        if (!isMounted) return;
        const list = Array.isArray(raw) ? raw : (raw?.data || []);
        setBrandOptions(
          list
            .map((b) => ({ _id: String(b._id || b.id || b.brandId || ""), name: b.name || b.brandName || b.title || "" }))
            .filter((b) => b._id && b.name)
        );
      })
      .catch(() => { if (isMounted) enqueueSnackbar("Failed to fetch brands", { variant: "error" }); })
      .finally(() => { if (isMounted) setLoadingBrands(false); });
    return () => { isMounted = false; };
  }, [open, planMode, enqueueSnackbar]);

  const handleCreatePlan = useCallback(() => {
    setPlanTouched({ planName: true, brand: true });
    if (!isNewPlanValid) return;
    createPlan(
      { name: newPlanName.trim(), brandId: newPlanBrand?._id, brandName: newPlanBrand?.name },
      {
        onSuccess: (data) => {
          enqueueSnackbar("Plan created", { variant: "success" });
          // Freshly created — no lists yet, Step 2 will naturally show its
          // "create a list" empty state.
          setSelectedPlan({ id: data.id, name: data.name });
          setPlanMode("pick");
        },
        onError: (error) => {
          enqueueSnackbar(error?.response?.data?.message || "Couldn't create the plan. Try again.", { variant: "error" });
        },
      }
    );
  }, [isNewPlanValid, newPlanName, newPlanBrand, createPlan, enqueueSnackbar]);

  // ── Step 2: List ────────────────────────────────────────────────────────
  const [listMode, setListMode] = useState("pick"); // 'pick' | 'create'
  const [selectedListId, setSelectedListId] = useState("");
  const [newListName, setNewListName] = useState("");

  const { data: summaryData, isLoading: summaryLoading } = usePlanSummary(selectedPlan?.id);
  const allLists = summaryData?.lists || [];
  const matchingLists = useMemo(
    () => allLists.filter((l) => l.platform === platform),
    [allLists, platform]
  );
  const selectedList = useMemo(() => matchingLists.find((l) => l.id === selectedListId) || null, [matchingLists, selectedListId]);
  const { mutate: createList, isPending: creatingList } = useCreateList(selectedPlan?.id);

  // Reset list selection whenever the plan changes, and default into "create"
  // mode automatically when there's nothing to pick from.
  useEffect(() => {
    setSelectedListId("");
    setListMode("pick");
    setNewListName("");
  }, [selectedPlan?.id]);

  useEffect(() => {
    if (!summaryLoading && matchingLists.length === 0) setListMode("create");
  }, [summaryLoading, matchingLists.length]);

  const handleCreateListSubmit = useCallback(() => {
    const trimmed = newListName.trim();
    if (!trimmed) return;
    createList(
      { name: trimmed, platform },
      {
        onSuccess: (data) => {
          enqueueSnackbar("List created", { variant: "success" });
          setSelectedListId(data.id);
          setListMode("pick");
        },
        onError: (error) => {
          enqueueSnackbar(error?.response?.data?.message || "Couldn't create the list. Try again.", { variant: "error" });
        },
      }
    );
  }, [newListName, platform, createList, enqueueSnackbar]);

  // ── Submit ──────────────────────────────────────────────────────────────
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);
  const { mutate: addInfluencers, isPending: adding } = useAddInfluencersByRapidId(selectedPlan?.id);

  const count = rapidInfluencerIds.length;
  const isValid = Boolean(selectedPlan?.id) && Boolean(selectedListId) && count > 0;

  const resetAll = useCallback(() => {
    setPlanMode("pick");
    setSelectedPlan(null);
    setNewPlanName("");
    setNewPlanBrand(null);
    setPlanTouched({ planName: false, brand: false });
    setListMode("pick");
    setSelectedListId("");
    setNewListName("");
    setDuplicateConfirm(null);
  }, []);

  const handleClose = useCallback(() => {
    resetAll();
    onClose?.();
  }, [resetAll, onClose]);

  const reportOutcome = (data) => {
    const addedCount = data?.added?.length ?? 0;
    const failed = data?.failed ?? [];
    const extra = failed.length > 1 ? ` (+${failed.length - 1} more)` : "";
    if (failed.length > 0) {
      if (addedCount === 0) {
        enqueueSnackbar(`Couldn't add any creators — ${failed[0].reason}${extra}`, { variant: "error" });
      } else {
        enqueueSnackbar(
          `${addedCount} creator${addedCount === 1 ? "" : "s"} added, ${failed.length} skipped — ${failed[0].reason}${extra}`,
          { variant: "warning" }
        );
      }
    } else if (addedCount > 0) {
      enqueueSnackbar(`${addedCount} creator${addedCount === 1 ? "" : "s"} added to "${selectedPlan?.name}".`, { variant: "success" });
    }
  };

  const handleSubmit = () => {
    if (!isValid) return;
    addInfluencers(
      { listId: selectedListId, platform, rapidInfluencerIds },
      {
        onSuccess: (data) => {
          reportOutcome(data);
          const duplicates = data?.duplicatesInPlan ?? [];
          if (duplicates.length > 0) {
            setDuplicateConfirm({ listId: selectedListId, duplicates });
            return;
          }
          if ((data?.added?.length ?? 0) > 0) {
            resetAll();
            onDone?.();
          }
        },
        onError: (error) => {
          enqueueSnackbar(error?.response?.data?.message || "Couldn't add these creators. Try again.", { variant: "error" });
        },
      }
    );
  };

  const handleConfirmDuplicates = () => {
    if (!duplicateConfirm) return;
    const { duplicates } = duplicateConfirm;
    setDuplicateConfirm(null);
    addInfluencers(
      {
        listId: selectedListId,
        platform,
        rapidInfluencerIds,
        confirmedUsernames: duplicates.map((d) => d.username),
      },
      {
        onSuccess: (data) => {
          const addedCount = data?.added?.length ?? 0;
          enqueueSnackbar(`${addedCount} creator${addedCount === 1 ? "" : "s"} added.`, { variant: "success" });
          resetAll();
          onDone?.();
        },
        onError: (error) => {
          enqueueSnackbar(error?.response?.data?.message || "Couldn't add these creators. Try again.", { variant: "error" });
        },
      }
    );
  };

  const previewCreators = selectedCreators.slice(0, 6);
  const previewNames = selectedCreators.slice(0, 2).map((c) => c.name || c.username).filter(Boolean);
  const previewExtra = count - previewNames.length;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        transitionDuration={{ enter: 320, exit: 250 }}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : { sm: 440, md: 500 },
            maxWidth: "100vw",
            borderRadius: isMobile ? 0 : "16px 0 0 16px",
            my: isMobile ? 0 : 2,
            mb: isMobile ? 0 : 2,
            height: isMobile ? "100%" : "calc(100% - 32px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.06)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Brand stripe */}
          <Box sx={{ height: 4, background: "linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)", flexShrink: 0 }} />

          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", px: 3, pt: 2.25, pb: 2, borderBottom: "1px solid #F3F4F6" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box
                sx={{
                  width: 38, height: 38, borderRadius: "10px", flexShrink: 0,
                  background: "linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.06))",
                  border: "1px solid rgba(255,82,0,0.18)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <RocketLaunchRoundedIcon sx={{ color: "#FF5200", fontSize: 19 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#111827", lineHeight: 1.2 }}>Add to Plan</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.4 }}>
                  <platformMeta.Icon sx={{ fontSize: 14, color: platformMeta.color }} />
                  <Typography sx={{ fontSize: 12.5, color: "#9CA3AF" }}>
                    {count} {platformMeta.label} creator{count === 1 ? "" : "s"} selected
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: "#9CA3AF", background: "#F9FAFB", border: "1px solid #E5E7EB", flexShrink: 0, "&:hover": { background: "#F3F4F6", color: "#374151" } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Avatar-stack preview of who's being added */}
          {previewCreators.length > 0 && (
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 1.5,
                borderBottom: "1px solid #F3F4F6",
                background: "linear-gradient(90deg, #FFF9F5 0%, #FFFDFB 100%)",
              }}
            >
              <AvatarGroup
                max={6}
                sx={{
                  "& .MuiAvatar-root": { width: 30, height: 30, fontSize: 12.5, fontWeight: 700, border: "2px solid #fff" },
                }}
              >
                {previewCreators.map((c) => (
                  <Avatar key={c.id} src={c.avatar || undefined} alt={c.name}>
                    {(c.name || c.username || "?").charAt(0).toUpperCase()}
                  </Avatar>
                ))}
              </AvatarGroup>
              <Typography sx={{ fontSize: 12.5, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {previewNames.map((n) => `@${n}`).join(", ")}
                {previewExtra > 0 && ` +${previewExtra} more`}
              </Typography>
            </Box>
          )}

          <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
            {/* Step 1 — Plan */}
            <Box sx={{ ...stepCardSx, mb: selectedPlan ? 2 : 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.75 }}>
                <StepBadge number={1} done={Boolean(selectedPlan) && planMode === "pick"} />
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Choose a Plan</Typography>
              </Box>

              {planMode === "pick" && selectedPlan ? (
                <Fade in key="plan-confirmed">
                  <Box>
                    <ConfirmedRow
                      title={selectedPlan.name}
                      subtitle={typeof selectedPlan.influencerCount === "number" ? `${selectedPlan.influencerCount} creators in this plan` : null}
                      onChange={() => setSelectedPlan(null)}
                    />
                  </Box>
                </Fade>
              ) : planMode === "pick" ? (
                <Fade in key="plan-pick">
                  <Box>
                    <Autocomplete
                      options={plans}
                      getOptionLabel={(option) => option?.name ?? ""}
                      value={selectedPlan}
                      loading={plansLoading}
                      onChange={(_, value) => setSelectedPlan(value)}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <span>{option.name}</span>
                          <Typography component="span" sx={{ fontSize: 12, color: "#9CA3AF" }}>
                            {option.influencerCount ?? 0} creators
                          </Typography>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search your plans"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 17, color: "#9CA3AF" }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <>
                                {plansLoading && <CircularProgress size={16} sx={{ color: "#FF5200" }} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ ...fieldSx, mb: 1.5 }}
                    />
                    <Button
                      startIcon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
                      onClick={() => setPlanMode("create")}
                      sx={linkButtonSx}
                    >
                      Create a new plan instead
                    </Button>
                  </Box>
                </Fade>
              ) : (
                <Fade in key="plan-create">
                  <Box>
                    {plans.length > 0 && (
                      <Button
                        startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
                        onClick={() => setPlanMode("pick")}
                        sx={{ ...linkButtonSx, color: "#6B7280", mb: 1.5, "&:hover": { background: "rgba(107,114,128,0.08)" } }}
                      >
                        Pick an existing plan instead
                      </Button>
                    )}
                    <Typography sx={{ ...sectionLabelSx, fontSize: 12.5 }}>Plan Name *</Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter plan name"
                      value={newPlanName}
                      onChange={(e) => setNewPlanName(e.target.value)}
                      onBlur={() => setPlanTouched((t) => ({ ...t, planName: true }))}
                      error={planTouched.planName && Boolean(planClientErrors.planName)}
                      helperText={planTouched.planName ? planClientErrors.planName : `${newPlanName.trim().length}/80`}
                      inputProps={{ maxLength: 80 }}
                      sx={fieldSx}
                    />
                    <Typography sx={{ ...sectionLabelSx, fontSize: 12.5 }}>Brand *</Typography>
                    <Autocomplete
                      options={brandOptions}
                      getOptionLabel={(option) => option?.name ?? ""}
                      value={newPlanBrand}
                      loading={loadingBrands}
                      disabled={loadingBrands}
                      onChange={(_, value) => setNewPlanBrand(value)}
                      onBlur={() => setPlanTouched((t) => ({ ...t, brand: true }))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select brand"
                          error={planTouched.brand && Boolean(planClientErrors.brand)}
                          helperText={planTouched.brand ? planClientErrors.brand : null}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingBrands && <CircularProgress size={16} sx={{ color: "#FF5200" }} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ ...fieldSx, mb: 1.5 }}
                    />
                    <Button variant="contained" disabled={creatingPlan} onClick={handleCreatePlan} sx={primaryButtonSx}>
                      {creatingPlan ? <CircularProgress size={18} color="inherit" /> : "Create Plan"}
                    </Button>
                  </Box>
                </Fade>
              )}
            </Box>

            {/* Step 2 — List (only once a plan is selected) */}
            <Collapse in={Boolean(selectedPlan)} timeout={320} unmountOnExit>
              <Box sx={stepCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.75 }}>
                  <StepBadge number={2} done={Boolean(selectedListId) && listMode === "pick"} />
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Choose a List</Typography>
                </Box>

                {summaryLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
                    <CircularProgress size={16} sx={{ color: "#FF5200" }} />
                    <Typography sx={{ fontSize: 13.5, color: "#9CA3AF" }}>Loading this plan's lists...</Typography>
                  </Box>
                ) : listMode === "pick" && selectedList ? (
                  <Fade in key="list-confirmed">
                    <Box>
                      <ConfirmedRow
                        title={selectedList.name}
                        subtitle={`${selectedList.influencerCount ?? 0} creators in this list`}
                        onChange={() => setSelectedListId("")}
                      />
                    </Box>
                  </Fade>
                ) : listMode === "pick" ? (
                  <Fade in key="list-pick">
                    <Box>
                      <Select
                        fullWidth
                        value={selectedListId}
                        onChange={(e) => setSelectedListId(e.target.value)}
                        displayEmpty
                        sx={{
                          mb: 1.5, background: "#F9FAFB", borderRadius: "10px", fontSize: 14.5,
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                        }}
                      >
                        <MenuItem value="" disabled>Select a list</MenuItem>
                        {matchingLists.map((list) => (
                          <MenuItem key={list.id} value={list.id}>
                            {list.name}
                            <Typography component="span" sx={{ ml: 1, color: "#9CA3AF", fontSize: 12.5 }}>
                              ({list.influencerCount} creators)
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        startIcon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
                        onClick={() => setListMode("create")}
                        sx={linkButtonSx}
                      >
                        Create a new list instead
                      </Button>
                    </Box>
                  </Fade>
                ) : (
                  <Fade in key="list-create">
                    <Box>
                      {matchingLists.length === 0 ? (
                        <EmptyHint
                          icon={FolderOpenRoundedIcon}
                          title={`No ${capitalize(platform)} list yet`}
                          subtitle={`"${selectedPlan?.name}" doesn't have a ${capitalize(platform)} list — create one below to get started.`}
                        />
                      ) : (
                        <Button
                          startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
                          onClick={() => setListMode("pick")}
                          sx={{ ...linkButtonSx, color: "#6B7280", mb: 1.5, "&:hover": { background: "rgba(107,114,128,0.08)" } }}
                        >
                          Pick an existing list instead
                        </Button>
                      )}
                      <TextField
                        fullWidth
                        placeholder="Enter list name"
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value.slice(0, 60))}
                        helperText={`${newListName.length}/60`}
                        sx={{ ...fieldSx, mb: 1.5 }}
                      />
                      <Button
                        variant="contained"
                        disabled={creatingList || !newListName.trim()}
                        onClick={handleCreateListSubmit}
                        sx={primaryButtonSx}
                      >
                        {creatingList ? <CircularProgress size={18} color="inherit" /> : `Create ${capitalize(platform)} List`}
                      </Button>
                    </Box>
                  </Fade>
                )}
              </Box>
            </Collapse>

            {/* Fills the space gracefully until a plan is picked, instead of blank whitespace */}
            {!selectedPlan && (
              <Fade in timeout={400}>
                <Box>
                  <EmptyHint
                    icon={PlaylistAddCheckRoundedIcon}
                    title="Pick a plan to continue"
                    subtitle="Once you've chosen or created a plan above, you'll pick which list these creators land in."
                  />
                </Box>
              </Fade>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ display: "flex", gap: 1.5, px: 3, py: 2, borderTop: "1px solid #F3F4F6", background: "#FAFAFA" }}>
            <Button
              fullWidth
              onClick={handleClose}
              sx={{
                color: "#6B7280", background: "#fff", borderRadius: "10px",
                py: 1.1, fontSize: 14, fontWeight: 500, textTransform: "none",
                border: "1px solid #E5E7EB", boxShadow: "none",
                "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB", color: "#374151" },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!isValid || adding}
              onClick={handleSubmit}
              startIcon={!adding && isValid ? <CheckCircleRoundedIcon sx={{ fontSize: 18 }} /> : null}
              sx={{ ...primaryButtonSx, py: 1.1, fontSize: 14 }}
            >
              {adding ? <CircularProgress size={20} color="inherit" /> : `Add ${count} Creator${count === 1 ? "" : "s"}`}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={Boolean(duplicateConfirm)} onClose={() => setDuplicateConfirm(null)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Already in another list?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {duplicateConfirm?.duplicates.length === 1 ? "This creator is" : "These creators are"} already in another
            list in this plan:
            <Box component="ul" sx={{ mt: 1, mb: 1.5, pl: 2.5 }}>
              {duplicateConfirm?.duplicates.map((d) => (
                <Box component="li" key={d.username} sx={{ fontSize: 14 }}>
                  <strong>{d.name || d.username}</strong> — already in "{d.existingListName}"
                </Box>
              ))}
            </Box>
            Add {duplicateConfirm?.duplicates.length === 1 ? "this creator" : "them"} here as well?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDuplicateConfirm(null)} sx={{ textTransform: "none", borderRadius: "10px" }}>
            Don't add
          </Button>
          <Button onClick={handleConfirmDuplicates} variant="contained" sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}>
            Add anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddToPlanDrawer;
